import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicService } from '../services/api';

const TenantContext = createContext(null);

const STORAGE_KEY = 'selectedTenant';

export function TenantProvider({ children, slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const [institution, setInstitution] = useState(null);
  const [loading, setLoading] = useState(Boolean(slug));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug) {
      setInstitution(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await publicService.getInstitution(slug);
        if (!cancelled) {
          setInstitution(res.data);
          localStorage.setItem(STORAGE_KEY, JSON.stringify({
            slug: res.data.slug,
            tenant_id: res.data.tenant_id,
            name: res.data.name,
            vertical: res.data.vertical,
          }));
        }
      } catch {
        if (!cancelled) {
          setError('Institución no encontrada');
          setInstitution(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [slug]);

  const value = useMemo(() => ({
    slug,
    institution,
    tenantId: institution?.tenant_id || null,
    loading,
    error,
    vertical: institution?.vertical || null,
    branding: institution ? {
      name: institution.citizen_portal_name || institution.name,
      subtitle: institution.citizen_portal_subtitle,
      primaryColor: institution.primary_color,
      logoUrl: institution.logo_url,
    } : null,
  }), [slug, institution, loading, error]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  return ctx || {
    slug: null,
    institution: null,
    tenantId: null,
    loading: false,
    error: null,
    vertical: null,
    branding: null,
  };
}

export function getStoredTenant() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
